import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CreateRoom from './CreateRoom';
import JoinRoom from './JoinRoom';
import ProfileSetup from './ProfileSetup';
import PlanningRoom from './PlanningRoom';
import Blog from './Blog';
import FAQ from './FAQ';
import Landing from './Landing';

const RoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { currentUser, participantId, login, joinRoom, leaveCurrentRoom } = useAuth();
  const [roomName, setRoomName] = useState('');
  const [needsProfile, setNeedsProfile] = useState(false);
  const [isJoiningRoom, setIsJoiningRoom] = useState(false);

  useEffect(() => {
    if (!roomId) {
      navigate('/');
      return;
    }

    // Set room ID in localStorage if not already set
    const currentRoomId = localStorage.getItem('currentRoomId');
    if (currentRoomId !== roomId) {
      localStorage.setItem('currentRoomId', roomId);
      localStorage.setItem('currentRoomName', `Room ${roomId.substring(0, 8)}`);
    }

    // Get room name from localStorage
    const currentRoomName = localStorage.getItem('currentRoomName');
    if (currentRoomName) {
      setRoomName(currentRoomName);
    } else {
      setRoomName(`Room ${roomId.substring(0, 8)}`);
    }
  }, [roomId, navigate]);

  // Separate effect for handling room joining to avoid infinite loops
  useEffect(() => {
    if (!roomId) return;

    const savedProfile = localStorage.getItem('planningPokerProfile');
    const currentRoomId = localStorage.getItem('currentRoomId');
    const needsProfileForNewRoom = localStorage.getItem('needsProfileForNewRoom') === 'true';
    const isRoomCreator = localStorage.getItem('isRoomCreator') === 'true';

    // Prevent multiple join attempts
    if (participantId || isJoiningRoom) {
      return;
    }

    if (needsProfileForNewRoom) {
      // Clear the flag and show profile setup for new room
      localStorage.removeItem('needsProfileForNewRoom');
      setNeedsProfile(true);
    } else if (savedProfile && currentRoomId === roomId) {
      // Only join if we're in the correct room and have a profile
      if (!currentUser && savedProfile) {
        // Auto-login with saved profile
        const profile = JSON.parse(savedProfile);
        setIsJoiningRoom(true);
        login(profile).then(() => {
          // Increased delay to ensure login is complete
          setTimeout(() => {
            if (!participantId && !isJoiningRoom) { // Double check to prevent race condition
              joinRoom(roomId, profile, isRoomCreator).finally(() => {
                setIsJoiningRoom(false);
              });
            } else {
              setIsJoiningRoom(false);
            }
          }, 1000);
        }).catch(() => {
          setNeedsProfile(true);
          setIsJoiningRoom(false);
        });
      } else if (currentUser && savedProfile) {
        // User is logged in but not joined to this room yet
        const profile = JSON.parse(savedProfile);
        setIsJoiningRoom(true);
        joinRoom(roomId, profile, isRoomCreator).catch(() => {
          setNeedsProfile(true);
        }).finally(() => {
          setIsJoiningRoom(false);
        });
      }
    } else if (!savedProfile) {
      // Need profile setup for new user or when joining via link
      setNeedsProfile(true);
    }
  }, [roomId, currentUser, participantId, login, joinRoom, isJoiningRoom]);

  const handleProfileComplete = async (profile: { name: string; role: any; avatar: string }) => {
    if (isJoiningRoom) {
      return;
    }

    try {
      const isRoomCreator = localStorage.getItem('isRoomCreator') === 'true';

      setIsJoiningRoom(true);
      await login(profile);

      if (roomId) {
        // Add small delay to ensure login is complete
        await new Promise(resolve => setTimeout(resolve, 300));
        await joinRoom(roomId, profile, isRoomCreator);
      }

      setNeedsProfile(false);
    } catch (error) {
      // Could show error message to user here
    } finally {
      setIsJoiningRoom(false);
    }
  };

  const handleLeaveRoom = async () => {
    await leaveCurrentRoom();
    navigate('/create');
  };

  if (!roomId) {
    return <div>Invalid room</div>;
  }

  if (needsProfile) {
    return (
      <ProfileSetup
        onComplete={handleProfileComplete}
        roomName={roomName}
      />
    );
  }

  if (!currentUser) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <PlanningRoom
        roomId={roomId}
        onLeaveRoom={handleLeaveRoom}
      />
    </div>
  );
};

const RedirectPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, participantId, loading } = useAuth();

  useEffect(() => {
    if (loading) return; // Wait for auth to load

    const currentRoomId = localStorage.getItem('currentRoomId');
    const savedProfile = localStorage.getItem('planningPokerProfile');

    // If user has active room session, redirect back to room
    if (currentRoomId && (participantId || (currentUser && savedProfile))) {
      navigate(`/room/${currentRoomId}`);
      return;
    }

    // No active room, redirect to create page as default
    navigate('/create');
  }, [currentUser, participantId, loading, navigate]);

  // Show loading while checking
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-600 flex items-center justify-center">
      <div className="text-white text-xl">Loading...</div>
    </div>
  );
};

// Main router component
const AppRouter: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/create" element={<CreateRoom />} />
        <Route path="/join" element={<JoinRoom />} />
        <Route path="/room/:roomId" element={<RoomPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/faq" element={<FAQ />} />
        {/* Redirect any unknown routes to create page */}
        <Route path="*" element={<RedirectPage />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
